import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { AoSGameState, GoodsColor, GoodsPlacementInfo } from '../../types';
import { executeProduction } from '../moves/executeProduction';
import { undoProductionPlacement } from '../moves/undoProductionPlacement';
import { resetProductionPlacement } from '../moves/resetProductionPlacement';

/**
 * Phase 9: 商品補充フェーズ（Goods Growth / Refill Goods）
 *
 * Barbados専用ロジック:
 * - 実行順序（仕様準拠）:
 *   1. Production アクション選択時: プレイヤーが袋から2個引いて商品補充表に配置
 *   2. Phase終了時（onEnd）: ダイスロールで自動補充（明側のみ2個）
 *
 * 参照:
 * - docs/specifications/logic/common/phase_09_goods_growth.md
 * - docs/specifications/logic/barbados/phase_09_goods_growth_override.md
 */
export const refillGoodsPhase: PhaseConfig<AoSGameState> = {
  // Phase開始時の処理
  onBegin: ({ G, ctx, random }: { G: AoSGameState; ctx: any; random: any }) => {
    debug.log('[refillGoodsPhase] ★★★ Phase 9: Refill Goods started ★★★');
    debug.log('[refillGoodsPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    const player = G.players[ctx.currentPlayer];

    // ========================================
    // Step 1: Production アクション選択時は袋から2個引く（ユーザーが配置）
    // ========================================
    if (player.selectedAction === 'PRODUCTION') {
      debug.log(`[refillGoodsPhase] Step 1: Production action selected, drawing 2 goods from bag`);

      // 配置カウント初期化
      G.productionPlacementCount = 0;

      // goodsDisplay のスナップショットを保存（Cancel 用）
      G.productionSnapshot = JSON.parse(JSON.stringify(G.goodsDisplay));

      // 袋から2個引く処理（自動処理）
      if (G.cubesInBag.length >= 2) {
        // Boardgame.io の乱数生成器を使用（リプレイ可能性のため）
        const shuffled = random.Shuffle(G.cubesInBag);
        const drawn: [GoodsColor, GoodsColor] = [shuffled[0], shuffled[1]];

        // 袋から削除
        G.cubesInBag = shuffled.slice(2);

        // drawnGoodsに保存
        G.drawnGoods = drawn;

        // ML学習用: Production時の袋引き結果を記録
        G.moveLog.push({
          turn: (G as any).actualTurn || 0,
          phase: ctx.phase || 'refillGoods',
          type: 'autoPhase',
          randomResults: { drawnGoods: drawn },
        });

        debug.log(`[refillGoodsPhase] Drew 2 goods from bag for Production: ${drawn.join(', ')}`);
        debug.log(`[refillGoodsPhase] Remaining cubes in bag: ${G.cubesInBag.length}`);
      } else if (G.cubesInBag.length === 1) {
        // 袋に1個しかない場合
        const drawn: [GoodsColor, GoodsColor] = [G.cubesInBag[0], G.cubesInBag[0]];
        G.cubesInBag = [];
        G.drawnGoods = drawn;
        console.warn(`[refillGoodsPhase] Only 1 cube in bag, drew the same cube twice: ${drawn[0]}`);
      } else {
        // 袋が空の場合
        G.drawnGoods = null;
        console.warn(`[refillGoodsPhase] Bag is empty, cannot draw goods for Production`);
      }

      // Production用のUI表示メッセージ
      G.autoPhaseResult = {
        phase: 'refillGoods',
        message: `Production: place 2 goods from bag onto the Goods Display.`,
        floatingText: `Production: Place 2 goods`,
        details: {},
      };

      // フェーズ開始フラグを設定
      (G as any).refillGoodsStarted = true;

      debug.log('[refillGoodsPhase] ★ Waiting for user to complete Production placement');
    } else {
      // ========================================
      // Production以外: 即座にダイスロールを実行
      // ========================================
      debug.log(`[refillGoodsPhase] No Production selected, executing dice roll immediately`);

      // ダイスロール実行
      const { diceRolls, placements } = executeDiceRollRefillInternal(G, random);

      // ML学習用: 自動フェーズのランダム結果を記録
      G.moveLog.push({
        turn: (G as any).actualTurn || 0,
        phase: ctx.phase || 'refillGoods',
        type: 'autoPhase',
        randomResults: { diceRolls, placements },
      });

      // 結果をゲーム状態に保存
      (G as any).lastDiceRolls = diceRolls;
      (G as any).lastGoodsPlacement = placements;

      // ダイスロール結果のみ表示してクリック待機
      const placementText = placements.length > 0
        ? placements.map(p => `${p.color} → ${p.cityName}`).join(', ')
        : 'No goods placed';

      G.autoPhaseResult = {
        phase: 'refillGoods',
        message: `Dice: ${diceRolls[0]}, ${diceRolls[1]}. ${placementText}`,
        floatingText: `🎲 ${diceRolls[0]}, ${diceRolls[1]}`,
        details: { diceRolls, placements },
      };

      // フェーズ開始フラグを設定（endIfで使用）
      (G as any).refillGoodsStarted = true;

      debug.log('[refillGoodsPhase] ★ Waiting for user click to proceed');
    }
  },

  // プレイヤーが操作可能なMove
  moves: {
    executeProduction,
    undoProductionPlacement,
    resetProductionPlacement,

    // Production配置完了後にダイスロールを実行（Finishボタン押下時に呼ばれる）
    triggerDiceRollForProduction: ({ G, random }: { G: AoSGameState; random: any }) => {
      debug.log('[triggerDiceRollForProduction] Executing dice roll after Production');

      // ダイスロール実行
      const { diceRolls, placements } = executeDiceRollRefillInternal(G, random);

      // 結果を保存
      (G as any).lastDiceRolls = diceRolls;
      (G as any).lastGoodsPlacement = placements;

      // UI表示用のautoPhaseResultを設定
      const placementText = placements.length > 0
        ? placements.map(p => `${p.color} → ${p.cityName}`).join(', ')
        : 'No goods placed';

      G.autoPhaseResult = {
        phase: 'refillGoods',
        message: `Dice: ${diceRolls[0]}, ${diceRolls[1]}. ${placementText}`,
        floatingText: `🎲 ${diceRolls[0]}, ${diceRolls[1]}`,
        details: { diceRolls, placements },
      };

      debug.log(`[triggerDiceRollForProduction] Dice rolls: ${diceRolls[0]}, ${diceRolls[1]}`);
      debug.log(`[triggerDiceRollForProduction] Placements: ${JSON.stringify(placements)}`);
    },

    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G }: { G: AoSGameState }) => {
      const player = G.players['0'];

      // Production選択時は productionExecuted を設定
      if (player.selectedAction === 'PRODUCTION') {
        G.productionExecuted = true;
        debug.log('[proceedAutoPhase] Production completed, setting productionExecuted = true');
      }

      G.autoPhaseResult = null;
    },
  },

  // ターン制御
  turn: {
    moveLimit: 2  // Production実行は2回（1個ずつ配置）
  },

  // Phase終了条件
  endIf: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    const player = G.players[ctx.currentPlayer];
    const started = (G as any).refillGoodsStarted === true;
    const resultCleared = G.autoPhaseResult === null;

    // Productionアクション選択時
    if (player.selectedAction === 'PRODUCTION') {
      // Production実行済み かつ autoPhaseResult がクリアされた（ユーザーがクリックした）
      const shouldEnd = G.productionExecuted === true && resultCleared;
      debug.log('[refillGoodsPhase] ★ endIf check (PRODUCTION): productionExecuted =', G.productionExecuted, ', autoPhaseResult =', G.autoPhaseResult ? 'exists' : 'null', ', shouldEnd =', shouldEnd);
      return shouldEnd;
    }

    // それ以外: フェーズが開始されてからautoPhaseResultがnullになるまで待機
    debug.log('[refillGoodsPhase] ★ endIf check (no PRODUCTION): started =', started, ', autoPhaseResult =', G.autoPhaseResult ? 'exists' : 'null');

    // フェーズ開始後、proceedAutoPhaseでautoPhaseResultがnullにされた場合のみ終了
    return started && resultCleared;
  },

  // Phase終了時の処理
  onEnd: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[refillGoodsPhase] ★★★ onEnd called - Phase 9 ending ★★★');
    debug.log('[refillGoodsPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });

    // Production選択時: ダイスロールは triggerDiceRollForProduction で既に実行済み

    // フラグのリセット
    if (G.productionExecuted) {
      G.productionExecuted = false;
      debug.log('[refillGoodsPhase] productionExecuted flag reset');
    }

    if (G.drawnGoods) {
      G.drawnGoods = null;
      debug.log('[refillGoodsPhase] drawnGoods reset');
    }

    if (G.productionPlacementCount !== undefined) {
      G.productionPlacementCount = 0;
      debug.log('[refillGoodsPhase] productionPlacementCount reset');
    }

    if (G.productionSnapshot) {
      G.productionSnapshot = null;
      debug.log('[refillGoodsPhase] productionSnapshot reset');
    }

    if (G.autoPhaseResult) {
      G.autoPhaseResult = null;
      debug.log('[refillGoodsPhase] autoPhaseResult reset');
    }

    if ((G as any).refillGoodsStarted) {
      (G as any).refillGoodsStarted = false;
      debug.log('[refillGoodsPhase] refillGoodsStarted flag reset');
    }

    // lastDiceRolls と lastGoodsPlacement もリセット
    if ((G as any).lastDiceRolls) {
      (G as any).lastDiceRolls = undefined;
      debug.log('[refillGoodsPhase] lastDiceRolls reset');
    }
    if ((G as any).lastGoodsPlacement) {
      (G as any).lastGoodsPlacement = undefined;
      debug.log('[refillGoodsPhase] lastGoodsPlacement reset');
    }

    debug.log('[refillGoodsPhase] ★★★ Next phase should be: advanceTurn');
  },

  // 次のPhaseへ
  next: 'advanceTurn'
};

/**
 * ダイスロールによる商品補充を実行（内部関数）
 *
 * @param G ゲーム状態
 * @param random Boardgame.io の乱数生成器
 * @returns ダイス結果と配置情報
 */
function executeDiceRollRefillInternal(
  G: AoSGameState,
  random: any
): { diceRolls: [number, number]; placements: GoodsPlacementInfo[] } {
  // Barbados: 明側のみ2個のダイスを振る
  const diceRolls: [number, number] = [random.Die(6), random.Die(6)];
  debug.log(`[executeDiceRollRefill] Dice rolls: ${diceRolls[0]}, ${diceRolls[1]}`);

  // 商品配置情報を初期化（アニメーション用）
  const placements: GoodsPlacementInfo[] = [];

  for (let i = 0; i < 2; i++) {
    debug.log(`[executeDiceRollRefill] Processing dice roll ${i + 1}: ${diceRolls[i]}`);
    placeGoodsFromDisplay(G, 'light', diceRolls[i], placements);
  }

  debug.log(`[executeDiceRollRefill] Goods placements: ${JSON.stringify(placements)}`);

  return { diceRolls, placements };
}

/**
 * 商品補充表から商品を取得してマップに配置
 *
 * 仕様（appendix_goods_display_board.md）:
 * - ダイス目に対応する列の「最も上段にある商品キューブ」を1個だけ配置
 * - 新都市列も同様に処理（3→A, 4→B, 5→C, 6→D for light / 1→E, 2→F, 3→G, 4→H for dark）
 *
 * @param G ゲーム状態
 * @param side 明側 or 暗側
 * @param diceRoll ダイス目（1-6）
 * @param placements 配置情報を記録する配列（アニメーション用）
 *
 * @see docs/reference/appendix_goods_display_board.md
 */
export function placeGoodsFromDisplay(
  G: AoSGameState,
  side: 'light' | 'dark',
  diceRoll: number,
  placements: GoodsPlacementInfo[]
): void {
  const columns = side === 'light' ? G.goodsDisplay.lightSide : G.goodsDisplay.darkSide;

  // 1. 都市列から1個配置
  const cityColumn = columns.find(col =>
    col.columnNumber === diceRoll &&
    col.cityId !== '' &&
    col.newCityId === null
  );

  if (cityColumn) {
    // 最上段の商品を1個取得
    const topIndex = cityColumn.cubes.findIndex(c => c !== null);
    if (topIndex !== -1) {
      const good = cityColumn.cubes[topIndex] as GoodsColor;
      cityColumn.cubes[topIndex] = null; // 取り出し

      // 対応する都市を検索
      const cityHex = G.map.find(hex =>
        hex.city !== null &&
        hex.city.id === cityColumn.cityId
      );

      if (cityHex) {
        cityHex.goods.push(good);
        debug.log(`[placeGoodsFromDisplay] Placed 1 ${good} in ${cityHex.city!.name} (dice: ${diceRoll}, city column, ${side})`);
        // アニメーション用の配置情報を記録
        placements.push({
          diceValue: diceRoll,
          color: good,
          cityName: cityHex.city!.name,
          cityId: cityColumn.cityId,
          cityType: 'city',
          side
        });
      } else {
        // 都市が見つからない場合は袋に戻す
        G.cubesInBag.push(good);
        debug.log(`[placeGoodsFromDisplay] City ${cityColumn.cityId} not found, returned ${good} to bag`);
      }
    } else {
      debug.log(`[placeGoodsFromDisplay] City column ${diceRoll} on ${side} side is empty`);
    }
  }

  // 2. 新都市列から1個配置（同じダイス目に対応する新都市列がある場合）
  //    Light側: 3→A, 4→B, 5→C, 6→D
  //    Dark側: 1→E, 2→F, 3→G, 4→H
  const newCityColumn = columns.find(col =>
    col.columnNumber === diceRoll &&
    col.newCityId !== null
  );

  if (newCityColumn) {
    // 最上段の商品を確認
    const topIndex = newCityColumn.cubes.findIndex(c => c !== null);
    if (topIndex !== -1) {
      // 対応する新都市タイルを検索（配置済みの場合のみ）
      // 注: newCity.id は "NEW_CITY_A" 形式、newCityColumn.newCityId は "A" 形式
      //     newCity.newCityId は "A" 形式なのでこちらを使用
      const newCityHex = G.map.find(hex =>
        hex.town !== null &&
        hex.town.isUrbanized &&
        hex.town.newCity !== null &&
        hex.town.newCity.newCityId === newCityColumn.newCityId
      );

      if (newCityHex) {
        // 新都市タイルが配置済み → 商品を取り出して配置
        const good = newCityColumn.cubes[topIndex] as GoodsColor;
        newCityColumn.cubes[topIndex] = null;
        newCityHex.goods.push(good);
        debug.log(`[placeGoodsFromDisplay] Placed 1 ${good} in new city ${newCityColumn.newCityId} (dice: ${diceRoll}, new city column, ${side})`);
        // アニメーション用の配置情報を記録
        placements.push({
          diceValue: diceRoll,
          color: good,
          cityName: newCityColumn.newCityId!, // 新都市ID
          cityId: newCityColumn.newCityId!,
          cityType: 'newCity',
          side
        });
      } else {
        // 新都市タイルが未配置 → 何もしない（商品は列に残る）
        debug.log(`[placeGoodsFromDisplay] New city ${newCityColumn.newCityId} not placed on map, goods remain in column`);
      }
    } else {
      debug.log(`[placeGoodsFromDisplay] New city column ${newCityColumn.newCityId} on ${side} side is empty`);
    }
  }
}
